// @ts-ignore
import Image from 'react-storefront/Image';
import { makeStyles } from '@material-ui/styles';
import theme from '../theme';
import { ShopwareImage, ShopwareProduct } from '@services/types/Shopware';
import { useState } from 'react';

const useStyles = makeStyles(() => ({
    image__wrapper: {
        flexGrow: 1,
        backgroundColor: theme.palette.primary.background,
        // @ts-ignore
        [theme.breakpoints.up('md')]: {
            '& img': {
                transition: 'opacity 0.25s ease',
            },
            '&:hover img': {
                opacity: 0.9,
            },
        },
    },
    image__background: {
        position: 'relative',
        flexShrink: 0,
        height: 'fit-content',
        '&:after': {
            content: '""',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            position: 'absolute',
            boxShadow: 'inset 0 0 90px 8px #f3f3f5',
            mixBlendMode: 'multiply',
            pointerEvents: 'none'
        }
    },
}), { name: 'ProductThumbnail' });

interface ProductThumbnailProps {
    image?: ShopwareImage,
    size?: number,
    index?: number,
    alt?: string,
    useBackground?: boolean,
    product?: ShopwareProduct | null,
    imageType?: 'standard' | 'environment',
    format?: 'list' | 'square-500',
    useHover?: boolean,
}

function getAspectRatio(format: ProductThumbnailProps['format']) {
    return format === 'list' ? 1.25 : 1;
}

export default function ProductThumbnail(
    {
        image,
        size = 550,
        index = 10,
        alt = 'Product Name',
        useBackground = false,
        product = null,
        imageType,
        format = 'list',
        useHover = false,
    }: ProductThumbnailProps,
) {
    const classes = useStyles();
    const [hover, setHover] = useState(false);

    if (useHover && hover) {
        imageType = 'environment';
    }

    function getThumbnailImageUrl() {
        if (product) {
            // @ts-ignore
            const category =  product?.extensions?.newport?.analytics?.item_category;
            if (imageType === undefined && (
                // @ts-ignore
                product?.extensions?.newport?.preferEnvironmentImage === true
                || category?.includes('Flower World')
                || category?.includes('Christmas')
                || category?.includes('Easter')
            )) {
                imageType = 'environment';
            }

            if (imageType === 'environment') {
                return {
                    src: `https://cdn-images.newporthome.eu/product/${product.id}/list-45-environment/${format}.jpg`,
                    width: 560,
                    height: 700,
                    aspectRatio: getAspectRatio(format),
                }
            }

            return {
                src: `https://cdn-images.newporthome.eu/product/${product.id}/list-45/${format}.jpg`,
                width: 560,
                height: 700,
                aspectRatio: getAspectRatio(format),
            }
        }

        const coverImage: any = image ? (image.media ? image.media : image) : [];

        if (coverImage.metaData && coverImage.metaData.width < 1200) {
            if (coverImage.metaData.width > 550) {
                console.info('Cover image is oversized size:', coverImage.metaData);
            }

            return {
                src: coverImage.url,
                width: coverImage.metaData.width,
                height: coverImage.metaData.height,
                aspectRatio: coverImage.metaData.height / coverImage.metaData.width,
            };
        }

        let thumbnail = getAppropriateImage(coverImage.thumbnails);

        return {
            src: thumbnail.url,
            width: thumbnail.width,
            height: thumbnail.height,
            aspectRatio: thumbnail.height / thumbnail.width,
        };
    }

    function getAppropriateImage(thumbnails: any) {
        let sizeDiff = { diff: 0, index: 0 };
        let image = null;

        if (thumbnails) {
            thumbnails.forEach((thumb: any, index: any) => {
                if (thumb.width === size) {
                    image = thumb;
                } else {
                    let currSizeDiff = Math.abs(thumb.width - size);

                    if (sizeDiff.diff === 0 || currSizeDiff < sizeDiff.diff) {
                        sizeDiff = { diff: currSizeDiff, index: index };
                    }
                }
            });
        }

        if (!image) {
            if (sizeDiff.diff > 0) {
                let firstImage = thumbnails[sizeDiff.index];
                image = { url: firstImage.url, width: firstImage.width, height: firstImage.height };
            } else {
                image = { url: '/images/placeholder/product.jpg', width: 550, height: 510 };
            }
        }

        return image;
    }

    return (
        <div onMouseOver={() => setHover(true)} onMouseLeave={() => setHover(false)}
             style={{position: 'relative', flexGrow: 1, height: 'fit-content'}}>
            <Image {...getThumbnailImageUrl()}
                   alt={alt}
                   contain
                   lazy={index > 7 ? true : 'ssr'}
                   lazyOffset={400}
                   notFoundSrc={'/images/placeholder/product.jpg'}
                   className={[classes.image__wrapper, useBackground ? classes.image__background : ''].join(' ')}
            />
        </div>
    );
}
